import React, { useRef, useState } from 'react';
import { Input, Modal, ModalBody, ModalContent } from '@nextui-org/react';
import { Buttons } from '../button';
import { getUrlUploadModel, uploadModel, uploadModelWithUrl } from '@/services';
import { FileUploader } from 'react-drag-drop-files';
import { IconClose, IconSuccessGreen, UploadFileIcon } from '@/assets';
import { toast } from 'react-toastify';
import Link from 'next/link';
import { useAuth } from '@/provider/UserProvider';
import { ProfileTabKey } from '@/contents/user-profile/content';
import { isValidUrl } from '@/utils';

const UploadModel: React.FC<{
  isOpen: boolean;
  onOpenChange: () => void;
  callback?: () => void;
}> = ({ isOpen, onOpenChange, callback }) => {
  const { auth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File>();
  const [link, setLink] = useState('');
  const refInput = useRef<any>();
  const [result, setResult] = useState('');

  const handleSubmit = async () => {
    try {
      if (loading) return;
      if (!file && !link) {
        toast.warning('Please import file or enter link to continue!');
        return;
      }
      setLoading(true);

      if (file) {
        const size = Math.round(file.size / 1000);
        const res = await getUrlUploadModel(size);
        const uploadUrl = res.data.uploadUrl;
        const imageUrl = res.data.imageUrl;
        if (!uploadUrl || !imageUrl) {
          toast.error('URL not found!');
          setLoading(false);
          return;
        };
        await uploadModelWithUrl(uploadUrl, file);
        await uploadModel(imageUrl);
      }

      if (link) {
        if (!isValidUrl(link)) {
          toast.warning('Link must be an URL address');
          setLoading(false);
          return;
        }
        await uploadModel(link);
      }

      setFile(undefined);
      setLink('');
      refInput.current.value = '';
      setResult('Upload model successfully!');
      setLoading(false);
      if (callback) callback();
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      placement="center"
      hideCloseButton
    >
      <ModalContent className="w-[500px] max-w-full">
        {(onClose) => (
          <ModalBody className="px-[30px] gap-0 py-[32px] sm:px-5 sm:py-5">
            <h4 className="font-semibold text-[16px] mb-3">Add model</h4>
            {file?.name ? (
              <div className="flex flex-col items-center justify-center rounded-[16px] border border-dashed border-gray-200 bg-gray-50 stroke-2 py-5">
                <UploadFileIcon className="mb-4" />
                <div className="flex items-center gap-2">
                  <IconSuccessGreen className="scale-[0.55] -mr-[10px]" />
                  <p className="text-14 font-semibold line-clamp-1 max-w-[200px] sm:max-w-[150px]">{file.name}</p>
                  <IconClose className="cursor-pointer" onClick={() => setFile(undefined)} />
                </div>
              </div>
            ) : (
              <FileUploader
                classes="[&>input]:w-full w-full"
                handleChange={(e: any) => { setFile(e); setResult('') }}
              >
                <div className="flex cursor-pointer flex-col items-center justify-center rounded-[16px] border border-dashed border-gray-200 bg-gray-50 stroke-2 py-5">
                  <UploadFileIcon className="mb-4" />
                  <span className="text-14 font-semibold">Drop your file here, or browse</span>
                </div>
              </FileUploader>
            )}
            <p className="my-2 text-center font-medium text-[14px]">OR</p>
            <Input
              classNames={{
                inputWrapper: 'rounded-lg bg-gray-50 sm:text-[13px] shadow-none ',
              }}
              ref={refInput}
              onValueChange={(e) => { setLink(e); setResult('') }}
              placeholder="Enter link download" 
            />
            {!!result && (
              <div className="flex items-center mt-4 gap-3 flex-wrap">
                <p className="text-green-500 font-medium text-[14px]">{result}</p>
                <Link href={`/profile/${auth.user.publicAddress}?tab=${ProfileTabKey.model}`} className="text-[14px] font-medium underline">View now</Link>
              </div>
            )}
            <div className="flex mt-5 gap-3 justify-end">
              <Buttons onClick={onClose} typeBtn="cancel">Cancel</Buttons>
              <Buttons isLoading={loading} onClick={handleSubmit}>Submit</Buttons>
            </div>
          </ModalBody>
        )}
      </ModalContent>
    </Modal>
  );
}

export default UploadModel;